import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

const WORKSHOPS = [
    { id: 1, title: 'Coagulation Insights', venue: 'Apollo BGS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=Apollo+BGS+Hospital+Mysuru' },
    { id: 2, title: '"Peak-a-boo"- Detecting hemoglobinopathy by HPLC', venue: 'Dept. of Biochemistry JSS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Hospital+Mysuru' },
    { id: 3, title: 'Hands on Flow Cytometry in Leukemia diagnosis: From sample to report', venue: 'USIC, JSS College of Pharmacy, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+College+of+Pharmacy+Mysuru' },
    { id: 4, title: 'Immunohematology & Apheresis: From Technique to therapy', venue: 'Blood Centre JSS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Hospital+Blood+Centre+Mysuru' },
    { id: 5, title: 'Procedural Skills- Bone marrow biopsy, Core Biopsy and FNAC', venue: 'Dept. of Anatomy JSS Medical College, Mysuru', spots: 25, mapUrl: 'https://maps.google.com/?q=JSS+Medical+College+Mysuru' },
    { id: 6, title: 'A journey through the gut-Interpretation of gastrointestinal biopsies', venue: 'Dept. of Pathology JSS Medical College, Mysuru', spots: 25, mapUrl: 'https://maps.google.com/?q=JSS+Medical+College+Mysuru' },
    { id: 7, title: 'Understanding Immunohistochemistry (IHC) -Multiutility tool or double-edged sword?', venue: 'Dept. of Pathology JSS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Hospital+Mysuru' },
    { id: 8, title: 'Utility of Immunofluorescence in Nephropathology & Dermatopathology', venue: 'Dept. of Pathology JSS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Hospital+Mysuru' },
    { id: 9, title: 'Digital pathology in practice-From Glass slides to AI-Ready Workflows', venue: 'Dept. of Pathology JSS Medical College, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Medical+College+Mysuru' },
    { id: 10, title: 'Mastering ANA-IF on HEp2 & Primate Liver as per new ICAP Guidelines (2024-25)', venue: 'Dept. of Microbiology JSS Hospital, Mysuru', spots: 20, mapUrl: 'https://maps.google.com/?q=JSS+Hospital+Mysuru' }
];

const CustomEventWorkshopRegistration = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab based on path
    const isWorkshop = location.pathname.includes('workshop');

    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);
    const [formData, setFormData] = useState({
        conferenceRegId: '',
        fullName: '',
        email: '',
        contactNumber: '',
        whatsappNumber: '',
        selectedWorkshopId: ''
    });

    const [verifiedConferenceTarget, setVerifiedConferenceTarget] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState('');

    // We'll store addon selections here: { addonId: boolean }
    const [selectedAddons, setSelectedAddons] = useState({});

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [hodCertFile, setHodCertFile] = useState(null);

    // Manual Payment States
    const [transactionReceiptFile, setTransactionReceiptFile] = useState(null);
    const [transactionReceiptPreview, setTransactionReceiptPreview] = useState(null);
    const transactionReceiptRef = useRef();

    const [submitted, setSubmitted] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    const [focused, setFocused] = useState('');
    const [loading, setLoading] = useState(false);
    const [showIntroModal, setShowIntroModal] = useState(true);

    const [workshopAvailabilities, setWorkshopAvailabilities] = useState({});

    const photoRef = useRef();
    const hodRef = useRef();

    useEffect(() => {
        const fetchAvailabilities = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/workshop/availabilities`);
                const data = await res.json();
                if (data.success && data.data) {
                    setWorkshopAvailabilities(data.data);
                }
            } catch (err) {
                console.error("Failed to fetch workshop availabilities", err);
            }
        };

        const fetchConfig = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/config`);
                const data = await res.json();
                if (data.success && data.data) {
                    setConfig(data.data);

                    // Initialize dynamic form fields
                    const initialData = {
                        fullName: '',
                        email: '',
                        contactNumber: '',
                        registrationCategory: '',
                    };
                    if (data.data.formFields) {
                        data.data.formFields.forEach(f => {
                            initialData[f.name] = '';
                        });
                    }
                    setFormData(initialData);
                }
            } catch (err) {
                console.error("Failed to fetch config", err);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
        fetchAvailabilities();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === 'selectedWorkshopId') {
                const wIdStr = value.toString();
                const availability = workshopAvailabilities[wIdStr];
                const totalSpots = availability?.total ?? (WORKSHOPS.find(w => w.id === parseInt(wIdStr))?.spots || 0);
                const bookedSpots = availability?.booked || 0;
                if (bookedSpots >= totalSpots) {
                    alert('We are sorry, but this workshop is fully booked and has no slots left.');
                    return prev;
                }
            }

            // If they change the workshop or the conference ID, nuke everything except the core UI state
            if (name === 'selectedWorkshopId' || name === 'conferenceRegId') {
                const initialResetData = {
                    fullName: '',
                    email: '',
                    contactNumber: '',
                    whatsappNumber: '',
                    conferenceRegId: name === 'conferenceRegId' ? value : prev.conferenceRegId,
                    selectedWorkshopId: name === 'selectedWorkshopId' ? value : prev.selectedWorkshopId,
                    transactionId: ''
                };

                // Keep config dynamically generated keys blank
                if (config && config.formFields) {
                    config.formFields.forEach(f => {
                        initialResetData[f.name] = '';
                    });
                }

                setVerifiedConferenceTarget(null);
                setVerificationError('');
                setSelectedAddons({});

                return initialResetData;
            }

            if (name === 'registrationCategory') {
                // Clear selected addons when category changes to re-evaluate exclusions
                setSelectedAddons({});
            }
            return updated;
        });
    };

    const handleAddonChange = (addonId, checked) => {
        setSelectedAddons(prev => ({ ...prev, [addonId]: checked }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleTransactionReceiptChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert("File size must be less than 5MB");
                return;
            }
            setTransactionReceiptFile(file);
            setTransactionReceiptPreview(URL.createObjectURL(file));
        }
    };

    const handleHodChange = (e) => {
        const file = e.target.files[0];
        if (file) setHodCertFile(file);
    };

    const verifyRegId = async () => {
        if (!formData.conferenceRegId) return;
        setVerifying(true);
        setVerificationError('');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/verify-conference/${formData.conferenceRegId}`);
            const data = await res.json();

            if (data.success && data.data) {
                const {
                    _id, __v, createdAt, updatedAt, registrationId,
                    isApproved, paymentStatus, transactionId,
                    transactionScreenshotUrl, amountAmount,
                    paymentAmount, registrationDate, dateOfPayment,
                    ...safeData
                } = data.data;

                setVerifiedConferenceTarget(data.data);
                setFormData(prev => ({
                    ...prev,
                    ...safeData,
                    transactionId: prev.transactionId,
                    conferenceRegId: prev.conferenceRegId,
                    selectedWorkshopId: prev.selectedWorkshopId,
                    fullName: data.data.fullName,
                    email: data.data.email,
                    contactNumber: data.data.contactNumber || data.data.whatsappNumber || '',
                    whatsappNumber: data.data.whatsappNumber || data.data.contactNumber || ''
                }));
            } else {
                setVerificationError(data.message || 'Invalid Registration ID or not found.');
                setVerifiedConferenceTarget(null);
            }
        } catch (err) {
            setVerificationError('Error verifying ID');
            setVerifiedConferenceTarget(null);
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.selectedWorkshopId) {
            alert("Please select a workshop");
            return;
        }

        const WID = parseInt(formData.selectedWorkshopId, 10);
        if (WID !== 10 && !verifiedConferenceTarget) {
            alert("Workshops 1-9 require a valid, approved Conference Registration ID");
            return;
        }

        if (!formData.fullName || !formData.email || !formData.contactNumber) {
            alert("Please fill all required base fields.");
            return;
        }

        // Payment validations
        if (!formData.transactionId || !formData.transactionId.trim()) {
            alert('Please enter your Transaction ID / UTR No. before submitting.');
            return;
        }
        if (!transactionReceiptFile) {
            alert('Please upload your payment screenshot / proof before submitting.');
            return;
        }

        setLoading(true);
        try {
            const selectedW = WORKSHOPS.find(w => w.id === WID);
            const submissionData = {
                ...formData,
                registrationCategory: `Workshop ${WID}: ${selectedW.title}`,
                amountPaid: "1500"
            };

            const finalAmount = 1500;

            const formDataToSend = new FormData();
            formDataToSend.append('data', JSON.stringify(submissionData));
            formDataToSend.append('paymentAmount', finalAmount);
            formDataToSend.append('transactionId', formData.transactionId || '');

            if (photoFile) formDataToSend.append('photo', photoFile);
            if (hodCertFile) formDataToSend.append('hodCert', hodCertFile);
            if (transactionReceiptFile) formDataToSend.append('transactionReceipt', transactionReceiptFile);

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/workshop/register`, {
                method: 'POST',
                body: formDataToSend
            });
            const regData = await res.json();

            if (regData.success) {
                setSubmitted(true);
                window.location.href = `/event/jss/workshop/participant/${regData.data.registrationId}`;
            } else {
                alert(regData.message || 'Registration failed');
            }

        } catch (error) {
            console.error(error);
            alert('An error occurred during submission');
        } finally {
            setLoading(false);
        }
    };

    // --- Pricing Calculation for Display --- //
    const computedPriceStr = formData.selectedWorkshopId ? '₹1,500' : null;

    if (configLoading) {
        return (
            <div className="min-h-screen flex text-[#5928ed] items-center justify-center bg-[#fcfaff]">
                <p className="font-semibold text-xl">Loading configuration...</p>
            </div>
        );
    }

    const inputBase = `w-full bg-white border-2 rounded-xl px-4 py-3 text-gray-800 text-sm font-medium transition-all duration-200 outline-none placeholder-gray-300`;
    const inputFocusCls = `border-[#5928ed] ring-4 ring-[#5928ed]/10`;
    const inputDefaultCls = `border-gray-200 hover:border-gray-300`;
    const getInputClass = (name) =>
        `${inputBase} ${focused === name ? inputFocusCls : inputDefaultCls}`;
    const getSelectClass = (name) =>
        `${inputBase} cursor-pointer appearance-none pr-10 ${focused === name ? inputFocusCls : inputDefaultCls}`;

    return (
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }} className="min-h-screen bg-gradient-to-br from-[#fcfaff] via-[#fef9f5] to-[#f5ede4]">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        .sans { font-family: 'DM Sans', sans-serif; }
        .pill-option input[type="radio"] { display: none; }
        .pill-option label {
          display: inline-flex; align-items: center; cursor: pointer;
          padding: 8px 18px; border-radius: 999px; border: 2px solid #e5e7eb;
          font-size: 13px; font-weight: 500; color: #6b7280;
          transition: all 0.2s; background: white;
          font-family: 'DM Sans', sans-serif;
        }
        .pill-option input[type="radio"]:checked + label {
          background: #5928ed; border-color: #5928ed; color: white;
          box-shadow: 0 4px 12px rgba(181,69,27,0.25);
        }
        .pill-option label:hover { border-color: #5928ed; color: #5928ed; }
        .pill-option input[type="radio"]:checked + label:hover { color: white; }
        .submit-btn {
          background: linear-gradient(135deg, #5928ed, #7244f7);
          transition: all 0.3s;
        }
        .submit-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(181,69,27,0.4); }
        .submit-btn:active { transform: translateY(0); }
        .contact-chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: white; border: 1.5px solid #f0e0d6;
          border-radius: 999px; padding: 6px 14px;
          font-size: 12.5px; color: #6b4c3b;
          font-family: 'DM Sans', sans-serif; font-weight: 500;
        }
        .fade-in { animation: fadeIn 0.35s ease; }
        @keyframes fadeIn { from { opacity:0; transform: translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .section-divider { height: 1px; background: linear-gradient(to right, transparent, #f0e0d6, transparent); }
        .price-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: linear-gradient(135deg, #5928ed, #7244f7);
          color: white; border-radius: 12px; padding: 10px 20px;
          font-family: 'DM Sans', sans-serif; font-weight: 700;
          font-size: 16px; box-shadow: 0 4px 14px rgba(181,69,27,0.3);
          animation: fadeIn 0.3s ease;
        }
        .select-wrapper { position: relative; }
        .select-wrapper::after {
          content: '▾'; position: absolute; right: 14px; top: 50%;
          transform: translateY(-50%); color: #9ca3af; pointer-events: none;
          font-size: 14px;
        }
        .upload-zone {
          border: 2px dashed #e5e7eb; border-radius: 16px;
          transition: all 0.2s; cursor: pointer; background: white;
        }
        .upload-zone:hover { border-color: #5928ed; background: #fcfaff; }
        .upload-zone-active { border-color: #5928ed; background: #fcfaff; }
        .disclaimer-box {
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border: 1.5px solid #fcd34d; border-radius: 16px;
        }
        .hod-box {
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #93c5fd; border-radius: 16px;
        }
      `}</style>
            <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12 relative z-10">
                {showIntroModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 fade-in">
                        <div className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full p-8 text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-blue-100 shadow-sm text-3xl">
                                ℹ️
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">Before you proceed</h2>
                            <p className="text-gray-600 mb-8 font-medium leading-relaxed text-[15px]">
                                A valid and approved <strong className="text-blue-600">Conference Registration ID</strong> is strictly required to register for <strong className="text-gray-800">Workshops 1 to 9</strong>.
                                <br /><br />
                                <strong className="text-[#5928ed]">Workshop 10</strong> is an exception and <strong className="underline decoration-[#5928ed] underline-offset-2">does not</strong> require a prior Conference Registration.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowIntroModal(false)}
                                className="w-full bg-blue-600 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/20 hover:-translate-y-0.5 text-white font-bold py-4 px-6 rounded-xl transition-all active:translate-y-0 active:scale-[0.98]"
                            >
                                I Understand
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Header ── */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6">
                    <div style={{ background: 'linear-gradient(135deg, #4013b5 0%, #5928ed 50%, #7244f7 100%)' }}
                        className="px-6 py-8 sm:px-10 sm:py-10 text-white text-center">
                        <div className="sans text-xs tracking-[0.2em]  opacity-80 mb-2 font-semibold">52nd ANNUAL STATE CONFERENCE</div>
                        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-1">KAPCON 2026</h1>
                        <div className="section-divider my-4 opacity-30" />
                        <p className="sans text-sm sm:text-base opacity-90 leading-relaxed">
                            DEPARTMENT OF PATHOLOGY, JSS MEDICAL COLLEGE<br />JSS AHER, MYSURU
                        </p>
                        <p className="sans text-xs opacity-70 mt-2">in association with</p>
                        <p className="sans text-xs font-semibold opacity-90 mt-1">KARNATAKA CHAPTER OF IAPM (KCIAPM)</p>
                    </div>
                    {/* <div className="px-6 py-5 sm:px-10 flex flex-col sm:flex-row gap-3 justify-center items-center bg-[#fcfaff]">
                        <div className="flex items-center gap-2 sans text-sm text-gray-600">
                            <span className="text-[#5928ed]">📍</span><span>JSS Medical College, Mysuru</span>
                        </div>
                        <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" />
                        <div className="flex items-center gap-2 sans text-sm text-gray-600">
                            <span className="text-[#5928ed]">📅</span><span>3rd – 5th July 2026</span>
                        </div>
                    </div> */}
                </div>

                {/* ── Contact ── */}
                <div className="bg-white rounded-2xl shadow-md px-6 py-5 sm:px-8 mb-6">
                    <p className="sans text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">Contact Us</p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { icon: '📞', text: 'Dr Deepika G · 9739320067' },
                            { icon: '📞', text: 'Dr Pallavi B R · 9632744696' },
                            { icon: '✉️', text: 'kapcon2026@gmail.com' },
                        ].map((c, i) => (
                            <span key={i} className="contact-chip"><span>{c.icon}</span>{c.text}</span>
                        ))}
                    </div>
                </div>

                {/* ── Navigation Tabs ── */}
                <div className="flex bg-white rounded-xl shadow-md p-1 mb-6 border border-gray-100">
                    <button
                        onClick={() => navigate('/event/jss/conference')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${!isWorkshop ? 'bg-[#5928ed] text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                        Registration for Conference
                    </button>
                    <button
                        onClick={() => navigate('/event/jss/workshop')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${isWorkshop ? 'bg-[#5928ed] text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                        Registration for Workshop
                    </button>
                </div>

                {/* ── Form ── */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="px-6 py-5 sm:px-10 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Registration Form</h2>
                            <p className="sans text-xs text-gray-400 mt-0.5">All fields marked * are required</p>
                        </div>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: 'linear-gradient(135deg, #5928ed, #7244f7)' }}>
                            <span className="text-white text-lg">📋</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="px-6 py-7 sm:px-10 space-y-5">

                        {/* ── Workshop Selection ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Workshop Selection (₹1,500 each)</p>
                        <div className="space-y-3">
                            {WORKSHOPS.map((w) => {
                                const availability = workshopAvailabilities[w.id.toString()];
                                const bookedCount = availability?.booked || 0;
                                const totalSpots = availability?.total ?? w.spots;
                                const remainingSpots = Math.max(0, totalSpots - bookedCount);
                                const isFull = remainingSpots === 0;

                                return (
                                    <label
                                        key={w.id}
                                        htmlFor={`w-${w.id}`}
                                        className={`flex items-start justify-between p-4 border rounded-xl transition-all ${isFull ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200' : formData.selectedWorkshopId == w.id ? 'border-[#5928ed] bg-[#fcfaff] cursor-pointer' : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'}`}
                                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                                    >
                                        <div className="flex flex-col gap-1.5">
                                            <span className="font-bold text-gray-800 text-base leading-tight pr-4">Workshop {w.id}: {w.title}</span>
                                            <span className={`text-sm font-medium flex items-center gap-1.5 flex-wrap ${isFull ? 'text-red-600' : 'text-gray-500'}`}>
                                                📍
                                                {w.mapUrl ? (
                                                    <a
                                                        href={w.mapUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={e => e.stopPropagation()}
                                                        className="text-[#5928ed] hover:underline font-semibold"
                                                    >
                                                        {w.venue}
                                                    </a>
                                                ) : (
                                                    <span>{w.venue}</span>
                                                )}
                                                <span className="text-gray-300">|</span>
                                                <span className={isFull ? 'text-red-600 font-semibold' : 'text-gray-500'}>{isFull ? 'Out of slots' : `👥 ${remainingSpots} spots left`}</span>
                                            </span>
                                        </div>
                                        <input
                                            type="radio"
                                            id={`w-${w.id}`}
                                            name="selectedWorkshopId"
                                            value={w.id}
                                            checked={formData.selectedWorkshopId == w.id}
                                            onChange={handleChange}
                                            disabled={isFull}
                                            className="w-5 h-5 mt-0.5 text-[#5928ed] border-[#5928ed] focus:ring-[#5928ed] shrink-0"
                                        />
                                    </label>
                                );
                            })}
                        </div>

                        {formData.selectedWorkshopId && formData.selectedWorkshopId != '10' && (
                            <div className="pt-2 fade-in">
                                <div className="bg-blue-50 pb-5 pt-4 px-5 border border-blue-100/80 rounded-2xl shadow-[inset_0_2px_10px_rgba(59,130,246,0.02)] space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-blue-500 text-lg">🔍</span>
                                        <p className="text-sm font-bold text-blue-900 tracking-wide">Conference Registration Required</p>
                                    </div>
                                    <p className="text-[13px] text-blue-700/80 font-medium pb-1 leading-relaxed">Please verify your active Conference Registration ID to proceed.</p>
                                    <div className="flex flex-col sm:flex-row gap-3 items-stretch w-full">
                                        <input type="text" name="conferenceRegId" value={formData.conferenceRegId} placeholder="Ex: KAP-ABC12"
                                            onChange={handleChange} disabled={verifying || verifiedConferenceTarget}
                                            className="w-full sm:flex-1 bg-white border border-blue-200/60 rounded-xl px-4 py-2.5 text-[15px] font-bold text-gray-800 uppercase tracking-widest outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/10 placeholder-blue-300/60 shadow-sm transition-all" />
                                        {!verifiedConferenceTarget ? (
                                            <button type="button" onClick={verifyRegId} disabled={verifying || !formData.conferenceRegId}
                                                className="w-full sm:w-auto shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-8 py-2.5 font-bold text-sm tracking-wide rounded-xl shadow-md disabled:shadow-none transition-all hover:scale-[1.02] active:scale-95">
                                                {verifying ? '...' : 'Verify'}
                                            </button>
                                        ) : (
                                            <button type="button" onClick={() => handleChange({ target: { name: 'conferenceRegId', value: '' } })}
                                                className="w-full sm:w-auto shrink-0 bg-white hover:bg-gray-50 border border-gray-200 text-gray-600 px-6 py-2.5 font-bold text-sm tracking-wide rounded-xl shadow-sm transition-all active:scale-95">
                                                Change Conference ID
                                            </button>
                                        )}
                                    </div>
                                    {verificationError && <div className="fade-in pt-1"><p className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-[13px] font-bold text-red-600 rounded-lg">{verificationError}</p></div>}
                                    {verifiedConferenceTarget && (
                                        <div className="fade-in pt-3 space-y-2">
                                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-[13px] font-bold text-green-700 rounded-lg">
                                                ✅ Identity Verified
                                            </div>
                                            {(verifiedConferenceTarget.registrationCategory || verifiedConferenceTarget.categoryIdNumber) && (
                                                <div className="bg-white/60 p-3 rounded-xl border border-blue-100 mt-2">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        {verifiedConferenceTarget.registrationCategory && (
                                                            <div>
                                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Category</p>
                                                                <p className="text-sm font-semibold text-gray-800">{verifiedConferenceTarget.registrationCategory}</p>
                                                            </div>
                                                        )}
                                                        {verifiedConferenceTarget.categoryIdNumber && (
                                                            <div>
                                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{verifiedConferenceTarget.idNumberLabel || 'ID Number'}</p>
                                                                <p className="text-sm font-semibold font-mono text-gray-800">{verifiedConferenceTarget.categoryIdNumber}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="section-divider" />

                        {(formData.selectedWorkshopId == '10' || verifiedConferenceTarget) && (
                            <div className="fade-in space-y-5">
                                {/* ── Personal Info ── */}
                                <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Personal Information</p>

                                <div>
                                    <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name *</label>
                                    <input type="text" name="fullName" value={formData.fullName} required placeholder="Dr. Jane Smith" readOnly={!!verifiedConferenceTarget}
                                        onChange={handleChange} onFocus={() => setFocused('fullName')} onBlur={() => setFocused('')}
                                        className={getInputClass('fullName') + (verifiedConferenceTarget ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                                </div>

                                <div>
                                    <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                                    <input type="email" name="email" value={formData.email} required placeholder="you@example.com" readOnly={!!verifiedConferenceTarget}
                                        onChange={handleChange} onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                                        className={getInputClass('email') + (verifiedConferenceTarget ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Contact Number *</label>
                                        <input type="tel" name="contactNumber" value={formData.contactNumber} required placeholder="+91 98765 43210" readOnly={!!verifiedConferenceTarget}
                                            onChange={handleChange} onFocus={() => setFocused('contactNumber')} onBlur={() => setFocused('')}
                                            className={getInputClass('contactNumber') + (verifiedConferenceTarget ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                                    </div>
                                    <div>
                                        <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">WhatsApp Number *</label>
                                        <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} required placeholder="+91 98765 43210" readOnly={!!verifiedConferenceTarget}
                                            onChange={handleChange} onFocus={() => setFocused('whatsappNumber')} onBlur={() => setFocused('')}
                                            className={getInputClass('whatsappNumber') + (verifiedConferenceTarget ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dynamic Render of Additional Information from formFields */}
                        {config?.formFields?.length > 0 && (
                            <>
                                <div className="section-divider" />
                                <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Additional Information</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {config.formFields.map(field => {
                                        let isRequired = field.required;
                                        let isHidden = false;

                                        const currentCategoryId = verifiedConferenceTarget?.categoryId;
                                        if (currentCategoryId && field.categoryRules) {
                                            const rule = field.categoryRules[currentCategoryId];
                                            if (rule === 'mandatory') isRequired = true;
                                            else if (rule === 'optional') isRequired = false;
                                            else if (rule === 'hidden') isHidden = true;
                                        }

                                        if (isHidden) return null;

                                        const colSpan = field.halfWidth ? "col-span-1" : "sm:col-span-2 col-span-1";

                                        return (
                                            <div key={field.id} className={colSpan}>
                                                <div className="flex items-end justify-between mb-1.5">
                                                    <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                        {field.label} {isRequired && '*'}
                                                    </label>
                                                    {field.isGovId && field.forgotIdUrl && (
                                                        <a href={field.forgotIdUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">
                                                            Forgot ID?
                                                        </a>
                                                    )}
                                                </div>

                                                {(field.type === 'text' || field.type === 'tel' || field.type === 'email') && (
                                                    <input
                                                        type={field.type}
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        required={isRequired}
                                                        placeholder={`Enter ${field.label}`}
                                                        onChange={handleChange}
                                                        readOnly={!!verifiedConferenceTarget && formData[field.name]?.length > 0}
                                                        onFocus={() => setFocused(field.name)}
                                                        onBlur={() => setFocused('')}
                                                        className={getInputClass(field.name) + (verifiedConferenceTarget && formData[field.name]?.length > 0 ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')}
                                                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                    />
                                                )}

                                                {field.type === 'textarea' && (
                                                    <textarea
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        required={isRequired}
                                                        placeholder={`Enter ${field.label}`}
                                                        rows={3}
                                                        readOnly={!!verifiedConferenceTarget && formData[field.name]?.length > 0}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocused(field.name)}
                                                        onBlur={() => setFocused('')}
                                                        className={getInputClass(field.name) + (verifiedConferenceTarget && formData[field.name]?.length > 0 ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')}
                                                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                    />
                                                )}

                                                {field.type === 'select' && (
                                                    <div className="select-wrapper">
                                                        <select
                                                            name={field.name}
                                                            value={formData[field.name] || ''}
                                                            required={isRequired}
                                                            onChange={handleChange}
                                                            disabled={!!verifiedConferenceTarget && formData[field.name]?.length > 0}
                                                            onFocus={() => setFocused(field.name)}
                                                            onBlur={() => setFocused('')}
                                                            className={getSelectClass(field.name) + (verifiedConferenceTarget && formData[field.name]?.length > 0 ? ' bg-gray-50 text-gray-500 pointer-events-none' : '')}
                                                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                        >
                                                            <option value="">Select option...</option>
                                                            {field.options?.split(',').map((opt, i) => {
                                                                const val = opt.trim();
                                                                return val ? <option key={i} value={val}>{val}</option> : null;
                                                            })}
                                                        </select>
                                                    </div>
                                                )}

                                                {field.type === 'radio' && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {field.options?.split(',').map((opt, i) => {
                                                            const val = opt.trim();
                                                            if (!val) return null;
                                                            return (
                                                                <div key={i} className="pill-option">
                                                                    <input
                                                                        type="radio"
                                                                        id={`${field.name}-${val}`}
                                                                        name={field.name}
                                                                        value={val}
                                                                        checked={formData[field.name] === val}
                                                                        onChange={handleChange}
                                                                        disabled={!!verifiedConferenceTarget && formData[field.name]?.length > 0}
                                                                        required={isRequired}
                                                                    />
                                                                    <label htmlFor={`${field.name}-${val}`} className={verifiedConferenceTarget && formData[field.name]?.length > 0 ? "opacity-60 cursor-not-allowed" : ""}>{val}</label>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        <div className="section-divider" />

                        {/* Ticket config removed entirely */}

                        <div className="section-divider" />

                        {/* ── Disclaimer & Payment Proof ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Payment Information</p>

                        {/* Bank Details Area */}
                        {formData.selectedWorkshopId && (
                            <div className="bg-white border-2 border-purple-100 rounded-xl p-5 shadow-sm fade-in space-y-4">
                                <div className="text-center bg-[#fcfaff] border border-[#f0d5c8] rounded-xl p-6 mb-4">
                                    <p className="text-sm text-gray-500 font-bold mb-1 uppercase tracking-wider" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>Your Total Registration Fee</p>
                                    <p className="text-4xl md:text-5xl font-black text-[#5928ed] tracking-tight" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>₹1,500</p>
                                </div>
                                <p className="sans text-sm font-semibold text-gray-800">Please transfer ₹1,500 to the following account:</p>

                                <div className="flex flex-col md:flex-row gap-6 items-center bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex-1 space-y-2 text-sm w-full font-mono text-gray-700">
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Account Name:</span><span className="font-bold">JSS Medical College Seminar A/c</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Account No:</span><span className="font-bold tracking-widest">64143204788</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">IFSC Code:</span><span className="font-bold uppercase">SBIN0040547</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Bank Name:</span><span className="font-bold">State Bank of India</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Branch:</span><span className="font-bold">Sri Shivarathreeshwara Nagara, Mysuru</span></div>
                                    </div>

                                    {/* QR Code */}
                                    <div className="flex flex-col items-center bg-white p-3 rounded-lg border border-gray-200 shadow-sm shrink-0">
                                        <img src="/payment-qr.png" alt="Scan to Pay" className="w-40 h-40 object-contain rounded" />
                                        <span className="sans text-[10px] text-gray-400 mt-2 font-medium uppercase tracking-wider">Scan to Pay</span>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-purple-100 flex flex-col gap-4">
                                    <div className="w-full">
                                        <label className="sans block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Transaction ID / UTR No. *</label>
                                        <input
                                            type="text"
                                            name="transactionId"
                                            value={formData.transactionId || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full text-sm py-2 px-3 border border-gray-300 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                            placeholder="Ex: UTR1234567890"
                                        />
                                    </div>

                                    <div className="w-full">
                                        <label className="sans block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Upload Payment Screenshot *</label>
                                        <input
                                            ref={transactionReceiptRef}
                                            type="file"
                                            accept="image/*,.pdf"
                                            className="hidden"
                                            onChange={handleTransactionReceiptChange}
                                        />
                                        <div
                                            onClick={() => transactionReceiptRef.current.click()}
                                            className={`p-2 border border-dashed rounded text-center cursor-pointer hover:bg-purple-50 transition-colors ${transactionReceiptFile ? 'bg-purple-50 border-purple-300 border-purple-400' : 'bg-white border-red-300'}`}
                                        >
                                            {transactionReceiptFile ? (
                                                <span className="text-xs font-medium text-purple-700 truncate block px-2">{transactionReceiptFile.name}</span>
                                            ) : (
                                                <span className="text-xs text-gray-500 font-medium flex items-center justify-center gap-1">
                                                    <span className="text-lg">📸</span> Attach Payment Proof (Required)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <p className="sans text-[10px] text-gray-500 italic mt-2 text-center">
                                    Note: Your registration will be reviewed and officially confirmed by the organizing committee upon payment verification.
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="pt-2 flex flex-col sm:flex-row gap-3">
                            <button type="submit" disabled={loading}
                                className="submit-btn flex-1 text-white font-semibold py-3.5 px-6 rounded-xl sans text-sm tracking-wide shadow-lg disabled:opacity-75 disabled:cursor-not-allowed">
                                {loading ? 'Processing...' : submitted ? '✓ Submitted Successfully!' : 'Verify & Register'}
                            </button>
                            <button type="button"
                                onClick={() => {
                                    setFormData({
                                        fullName: '', email: '', contactNumber: '', whatsappNumber: '',
                                        organization: '', designation: '', qualification: '', otherQualification: '',
                                        areaOfExpertise: '', otherAreaOfExpertise: '', foodChoice: '',
                                        registrationCategory: '', registrationDate: '', accompanyingSpouse: '',
                                        participantType: '',
                                    });
                                    setPhotoFile(null);
                                    setPhotoPreview(null);
                                    setHodCertFile(null);
                                }}
                                className="sans text-sm font-medium text-gray-400 hover:text-gray-600 py-3.5 px-6 rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-all">
                                Clear form
                            </button>
                        </div>

                    </form>
                </div>

                <p className="sans text-center text-xs text-gray-400 mt-6">© 2026 KAPCON · JSS Medical College, Mysuru</p>
            </div>
        </div>
    );
};

export default CustomEventWorkshopRegistration;